import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import type { AppBaseConfig } from '@streamlain/view-config';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  eyebrow: z.string().default(''),
  title: z.string().default(''),
  subtitle: z.string().default(''),
});

function AppBaseHeaderRenderer({
  config,
  runtimeContext,
}: ViewComponentRenderProps) {
  const typedConfig = config as Partial<z.infer<typeof configSchema>>;
  const appBaseConfig = readAppBaseConfig(runtimeContext);
  const appBaseDisplayName = readAppBaseDisplayName(runtimeContext);
  const pageTitle = readPageTitle(runtimeContext, appBaseConfig);
  const viewTitle =
    typeof runtimeContext?.view?.['title'] === 'string'
      ? runtimeContext.view['title'].trim()
      : '';
  const viewDescription =
    typeof runtimeContext?.view?.['description'] === 'string'
      ? runtimeContext.view['description'].trim()
      : '';
  const title =
    readString(typedConfig.title).trim() ||
    pageTitle ||
    viewTitle ||
    appBaseDisplayName;
  const eyebrow = readString(typedConfig.eyebrow).trim() || appBaseDisplayName;
  const subtitle = readString(typedConfig.subtitle).trim() || viewDescription;

  return createElement(
    'section',
    {
      className: styles.appBaseHeader,
      'data-sl-role': 'app-base-header',
    },
    eyebrow && eyebrow !== title
      ? createElement(
          'div',
          {
            className: styles.appBaseHeaderEyebrow,
            'data-sl-field': 'eyebrow',
          },
          eyebrow
        )
      : null,
    createElement(
      'h1',
      { className: styles.appBaseHeaderTitle, 'data-sl-field': 'title' },
      title
    ),
    subtitle
      ? createElement(
          'p',
          {
            className: styles.appBaseHeaderSubtitle,
            'data-sl-field': 'subtitle',
          },
          subtitle
        )
      : null
  );
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readAppBaseConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): AppBaseConfig | null {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'config' in appBase
      ? (appBase as { config?: unknown }).config
      : null;
  return raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as AppBaseConfig).pages) &&
    Array.isArray((raw as AppBaseConfig).navigation)
    ? (raw as AppBaseConfig)
    : null;
}

function readAppBaseDisplayName(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): string {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'branding' in appBase
      ? (appBase as { branding?: unknown }).branding
      : null;
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  const settings =
    record?.['settings'] &&
    typeof record['settings'] === 'object' &&
    !Array.isArray(record['settings'])
      ? (record['settings'] as Record<string, unknown>)
      : null;
  const appBaseDisplayName =
    typeof settings?.['displayName'] === 'string'
      ? settings['displayName'].trim()
      : '';
  return appBaseDisplayName || BRAND_DISPLAY_NAME;
}

function readPageTitle(
  runtimeContext: ViewComponentRenderProps['runtimeContext'],
  appBaseConfig: AppBaseConfig | null
): string {
  const page = runtimeContext?.page;
  const titleFormula =
    page &&
    typeof page === 'object' &&
    'titleFormula' in page &&
    page.titleFormula &&
    typeof page.titleFormula === 'object'
      ? (page.titleFormula as Record<string, unknown>)
      : null;
  if (
    titleFormula?.['type'] === 'value' &&
    typeof titleFormula['value'] === 'string'
  ) {
    return titleFormula['value'].trim();
  }

  const pageId =
    page && typeof page === 'object' && typeof page['id'] === 'string'
      ? page['id']
      : null;
  if (!pageId || !appBaseConfig) return '';
  const navItem = appBaseConfig.navigation.find(item => item.pageId === pageId);
  const navLabel = navItem?.labelFormula;
  return navLabel?.type === 'value' ? String(navLabel.value).trim() : '';
}

export const AppBaseHeaderComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.APP_BASE_HEADER,
  type: 'navigation',
  name: 'AppBase Header',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    'H'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    eyebrow: '',
    title: '',
    subtitle: '',
  },
  render: AppBaseHeaderRenderer,
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'H Header'
    );
  },
};
